
      const STORAGE_KEY = 'webxr-feature-combinator-v1';
      const LOG_PREFIX = '[WXR-ISO]';

      // EARLY: apply Chromium projection-layer workaround if previously
      // enabled. Must run before three.js touches any WebXR API. Reading
      // sessionStorage and deleting the prototype members is synchronous,
      // so doing it before the dynamic imports below is sufficient.
      //
      // NOTE: this is an intentional standalone copy of the production helper
      // gps-plus-slam-app-framework/ar/chromium-camera-access-workaround.ts.
      // It is deliberately NOT reused here: the production helper always
      // applies the deletes and only adds the baseLayer-persistence patch on
      // the affected Chrome window, but this diagnostic page must be able to
      // force BOTH toggles ON/OFF independently on ANY Chrome build to bisect
      // on-device. The logic below (delete + baseLayer-persistence merge
      // order) mirrors the helper; keep them in sync when the helper changes.
      let workaroundApplied = {
        createProjectionLayer: false,
        layers: false,
        baseLayerPersistence: false,
      };
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const state = raw ? JSON.parse(raw) : null;
        if (state?.workaround) {
          if (
            typeof XRWebGLBinding !== 'undefined' &&
            'createProjectionLayer' in XRWebGLBinding.prototype
          ) {
            delete XRWebGLBinding.prototype.createProjectionLayer;
            workaroundApplied.createProjectionLayer = true;
          }
          if (
            typeof XRRenderState !== 'undefined' &&
            'layers' in XRRenderState.prototype
          ) {
            delete XRRenderState.prototype.layers;
            workaroundApplied.layers = true;
          }
          console.log(
            `${LOG_PREFIX} applied projection-layer workaround:`,
            workaroundApplied
          );
        }
        // Chrome 148.0.7778.12+ defeated the plain projection-layer workaround
        // above. Additionally persist the baseLayer reference across
        // updateRenderState calls so three.js's later depthNear/depthFar update
        // does not drop the active glBaseLayer (which triggers the crash on the
        // camera-texture access). Stop-gap until Chrome M149 ships the real fix.
        // See three.js#33404 (m0nzderr, 2026-05) and crbug.com/507508099.
        if (state?.baseLayerPersistence && typeof XRSession !== 'undefined') {
          const originalUpdateRenderState =
            XRSession.prototype.updateRenderState;
          // Track the last-seen baseLayer PER XRSession instance (keyed on the
          // call's `this`), mirroring the production helper. A single shared
          // variable would leak a previous session's baseLayer into a later
          // session: an XRWebGLLayer is bound to the session it was created
          // for, so re-supplying a stale layer to a different session throws
          // InvalidStateError. The WeakMap also lets entries be GC'd with their
          // sessions.
          const lastBaseLayerBySession = new WeakMap();
          XRSession.prototype.updateRenderState = function (rawOptions) {
            // Normalize null/undefined/non-object to {} so an explicit
            // updateRenderState(null) does not throw on options.baseLayer
            // (native WebXR coerces a null dictionary arg to an empty one; a
            // default param value only covers undefined). Mirrors the helper.
            const options =
              typeof rawOptions === 'object' && rawOptions !== null
                ? rawOptions
                : {};
            if (typeof this !== 'object' || this === null) {
              return originalUpdateRenderState.call(this, options);
            }
            if (options.baseLayer !== undefined) {
              lastBaseLayerBySession.set(this, options.baseLayer);
            }
            const remembered = lastBaseLayerBySession.get(this);
            // Spread options FIRST so the restored baseLayer always wins; an
            // explicit baseLayer: undefined in options must not clobber it.
            return originalUpdateRenderState.call(
              this,
              remembered !== undefined
                ? { ...options, baseLayer: remembered }
                : options
            );
          };
          workaroundApplied.baseLayerPersistence = true;
          console.log(`${LOG_PREFIX} applied baseLayer-persistence workaround`);
        }
      } catch {
        /* ignore — workaround is best-effort */
      }

      const THREE = await import('three');
      const { ARButton } = await import('three/addons/webxr/ARButton.js');

      const logEl = document.getElementById('log');
      const payloadEl = document.getElementById('payload-preview');
      const slot = document.getElementById('ar-button-slot');
      const startBtn = document.getElementById('start-ar');

      const requiredInputs = document.querySelectorAll(
        'input[name="required"]'
      );
      const optCameraAccess = document.getElementById('opt-camera-access');
      const optDomOverlay = document.getElementById('opt-dom-overlay');
      const optDepthSensing = document.getElementById('opt-depth-sensing');
      const optCameraTextureRead = document.getElementById(
        'opt-camera-texture-read'
      );
      const optWorkaround = document.getElementById(
        'opt-projection-layer-workaround'
      );
      const optBaseLayer = document.getElementById(
        'opt-baselayer-persistence-workaround'
      );
      const workaroundStatus = document.getElementById('workaround-status');
      const baseLayerStatus = document.getElementById('baselayer-status');
      const cameraTextureReadStatus = document.getElementById(
        'camera-texture-read-status'
      );

      function log(msg, cls) {
        const stamp = new Date().toISOString().slice(11, 23);
        const line = document.createElement('div');
        if (cls) line.className = cls;
        line.textContent = `${stamp} ${msg}`;
        logEl.appendChild(line);
        logEl.scrollTop = logEl.scrollHeight;
        // eslint-disable-next-line no-console -- diagnostic page
        console.log(`${LOG_PREFIX} ${msg}`);
      }

      function getRequired() {
        for (const input of requiredInputs) {
          if (input.checked) return input.value;
        }
        return 'hit-test';
      }

      function buildSessionInit() {
        const required = getRequired();
        const optional = [];
        if (optCameraAccess.checked) optional.push('camera-access');
        if (optDomOverlay.checked) optional.push('dom-overlay');
        if (optDepthSensing.checked) optional.push('depth-sensing');
        const init = { requiredFeatures: [required] };
        if (optional.length) init.optionalFeatures = optional;
        if (optDomOverlay.checked) {
          init.domOverlay = { root: document.body };
        }
        if (optDepthSensing.checked) {
          init.depthSensing = {
            usagePreference: ['cpu-optimized'],
            dataFormatPreference: ['luminance-alpha', 'float32'],
          };
        }
        return init;
      }

      function serializablePayload(init) {
        // Replace the DOM root with a placeholder string so JSON.stringify works.
        const copy = { ...init };
        if (copy.domOverlay) copy.domOverlay = { root: '<body>' };
        return copy;
      }

      function updatePayloadPreview() {
        const init = buildSessionInit();
        payloadEl.textContent = JSON.stringify(
          serializablePayload(init),
          null,
          2
        );
      }

      function saveState() {
        const state = {
          required: getRequired(),
          cameraAccess: optCameraAccess.checked,
          domOverlay: optDomOverlay.checked,
          depthSensing: optDepthSensing.checked,
          cameraTextureRead: optCameraTextureRead.checked,
          workaround: optWorkaround.checked,
          baseLayerPersistence: optBaseLayer.checked,
        };
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
          /* ignore */
        }
      }

      function loadState() {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY);
          if (!raw) return;
          const state = JSON.parse(raw);
          for (const input of requiredInputs) {
            input.checked = input.value === state.required;
          }
          optCameraAccess.checked = !!state.cameraAccess;
          optDomOverlay.checked = !!state.domOverlay;
          optDepthSensing.checked = !!state.depthSensing;
          optCameraTextureRead.checked = !!state.cameraTextureRead;
          optWorkaround.checked = !!state.workaround;
          optBaseLayer.checked = !!state.baseLayerPersistence;
        } catch {
          /* ignore */
        }
      }

      for (const input of [
        ...requiredInputs,
        optCameraAccess,
        optDomOverlay,
        optDepthSensing,
      ]) {
        input.addEventListener('change', () => {
          saveState();
          updatePayloadPreview();
        });
      }

      // Camera-texture read is a render-loop flag, not a session-init flag, so
      // it does NOT reload the page. Toggling it takes effect on the next
      // animation frame.
      function updateCameraTextureReadStatus() {
        if (!optCameraTextureRead.checked) {
          cameraTextureReadStatus.textContent = '';
        } else if (!optCameraAccess.checked) {
          cameraTextureReadStatus.textContent =
            ' — enable camera-access above for this to do anything';
        } else {
          cameraTextureReadStatus.textContent =
            ' — will read the camera image each frame';
        }
      }
      optCameraTextureRead.addEventListener('change', () => {
        saveState();
        updateCameraTextureReadStatus();
      });
      optCameraAccess.addEventListener('change', updateCameraTextureReadStatus);

      // Workaround toggle: save state then reload so the early `delete`
      // block at the top of the script can run with the new value before
      // any WebXR API is touched.
      optWorkaround.addEventListener('change', () => {
        saveState();
        workaroundStatus.textContent = ' — reloading…';
        setTimeout(() => location.reload(), 50);
      });

      optBaseLayer.addEventListener('change', () => {
        saveState();
        baseLayerStatus.textContent = ' — reloading…';
        setTimeout(() => location.reload(), 50);
      });

      loadState();
      updatePayloadPreview();
      if (optWorkaround.checked) {
        workaroundStatus.textContent =
          workaroundApplied.createProjectionLayer || workaroundApplied.layers
            ? ' — applied this load'
            : ' — enabled but globals absent on this device';
      }
      if (optBaseLayer.checked) {
        baseLayerStatus.textContent = workaroundApplied.baseLayerPersistence
          ? ' — applied this load'
          : ' — enabled but XRSession absent on this device';
      }
      updateCameraTextureReadStatus();

      // Three.js scene setup — intentionally identical in shape to the upstream
      // webxr_ar_hittest.html example.
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        70,
        window.innerWidth / window.innerHeight,
        0.01,
        20
      );

      const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 3);
      light.position.set(0.5, 1, 0.25);
      scene.add(light);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.xr.enabled = true;
      renderer.domElement.style.position = 'fixed';
      renderer.domElement.style.inset = '0';
      renderer.domElement.style.zIndex = '-1';
      document.body.appendChild(renderer.domElement);

      const reticle = new THREE.Mesh(
        new THREE.RingGeometry(0.15, 0.2, 32).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial()
      );
      reticle.matrixAutoUpdate = false;
      reticle.visible = false;
      scene.add(reticle);

      let hitTestSource = null;
      let hitTestSourceRequested = false;
      let frameCount = 0;
      let lastFrameLogTime = 0;
      // State for the per-frame camera-texture read (the production-app crash
      // path the plain reproducer otherwise skips). Reset per session in
      // rebuildArButton so each AR entry re-logs the grant status once.
      let cameraReadLoggedGrant = false;
      let cameraTextureNullCount = 0;

      /**
       * Mirror the production frame loop's camera usage. Three.js clears its
       * per-frame camera-texture cache each animation frame and only calls
       * `XRWebGLBinding.getCameraImage(view.camera)` lazily when the app calls
       * `renderer.xr.getCameraTexture(view.camera)`. The plain reproducer
       * REQUESTS `camera-access` but never calls it, so it never hits
       * `getCameraImage` — the exact `camera_helper_` null-deref in
       * crbug.com/507508099. The production apps DO call it every frame. This
       * function reproduces that, so the page exercises the same crash surface.
       */
      function readCameraTextureForCrashRepro(frame, referenceSpace) {
        const pose = frame.getViewerPose(referenceSpace);
        if (!pose) return;
        const view = pose.views[0];
        const xrCamera = view && view.camera;
        if (!xrCamera) {
          if (!cameraReadLoggedGrant) {
            cameraReadLoggedGrant = true;
            log(
              'camera-texture read: XRView.camera is undefined — camera-access not granted?',
              'err'
            );
          }
          return;
        }
        if (!cameraReadLoggedGrant) {
          cameraReadLoggedGrant = true;
          log(
            `camera-texture read: XRView.camera ${xrCamera.width}x${xrCamera.height} — calling getCameraTexture()`,
            'ok'
          );
        }
        // This call triggers XRWebGLBinding.getCameraImage() inside three.js.
        const texture = renderer.xr.getCameraTexture(xrCamera);
        if (!texture && cameraTextureNullCount < 5) {
          cameraTextureNullCount++;
          log(
            `getCameraTexture() returned null/undefined (${cameraTextureNullCount}/5)`,
            'info'
          );
        }
      }

      function animate(_timestamp, frame) {
        if (frame) {
          frameCount++;
          const now = performance.now();
          if (now - lastFrameLogTime > 1000) {
            log(`frame #${frameCount}`, 'info');
            lastFrameLogTime = now;
          }

          const referenceSpace = renderer.xr.getReferenceSpace();
          const session = renderer.xr.getSession();

          const required = getRequired();
          if (required === 'hit-test' && !hitTestSourceRequested) {
            session.requestReferenceSpace('viewer').then((viewerSpace) => {
              session
                .requestHitTestSource({ space: viewerSpace })
                .then((source) => {
                  hitTestSource = source;
                  log('hit-test source acquired', 'ok');
                })
                .catch((err) => log(`hit-test source error: ${err}`, 'err'));
            });
            session.addEventListener('end', () => {
              hitTestSourceRequested = false;
              hitTestSource = null;
            });
            hitTestSourceRequested = true;
          }

          if (hitTestSource && referenceSpace) {
            const results = frame.getHitTestResults(hitTestSource);
            if (results.length) {
              const hit = results[0];
              reticle.visible = true;
              reticle.matrix.fromArray(
                hit.getPose(referenceSpace).transform.matrix
              );
            } else {
              reticle.visible = false;
            }
          }

          // Production-app camera usage: read the camera image every frame.
          // Independent of hit-test so it can be bisected on its own.
          if (optCameraTextureRead.checked && referenceSpace) {
            readCameraTextureForCrashRepro(frame, referenceSpace);
          }
        }
        renderer.render(scene, camera);
      }
      renderer.setAnimationLoop(animate);

      window.addEventListener('resize', () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      });

      window.addEventListener('error', (event) => {
        log(`window error: ${event.message}`, 'err');
      });
      window.addEventListener('unhandledrejection', (event) => {
        log(`unhandled rejection: ${event.reason}`, 'err');
      });

      function rebuildArButton() {
        slot.replaceChildren();
        frameCount = 0;
        hitTestSource = null;
        hitTestSourceRequested = false;
        cameraReadLoggedGrant = false;
        cameraTextureNullCount = 0;
        const init = buildSessionInit();
        log(
          `creating ARButton with ${JSON.stringify(serializablePayload(init))}`,
          'info'
        );
        const btn = ARButton.createButton(renderer, init);
        btn.addEventListener('click', () => log('ARButton clicked', 'info'));
        slot.appendChild(btn);
      }

      renderer.xr.addEventListener('sessionstart', () => {
        log('sessionstart', 'ok');
      });
      renderer.xr.addEventListener('sessionend', () => {
        log(`sessionend (total frames: ${frameCount})`, 'info');
      });

      startBtn.addEventListener('click', rebuildArButton);

      log(`ready. Three.js r${THREE.REVISION}`, 'ok');
      if (workaroundApplied.createProjectionLayer || workaroundApplied.layers) {
        log(
          `Chromium projection-layer workaround active (deleted: ${[
            workaroundApplied.createProjectionLayer && 'createProjectionLayer',
            workaroundApplied.layers && 'XRRenderState.layers',
          ]
            .filter(Boolean)
            .join(', ')})`,
          'info'
        );
      } else if (optWorkaround.checked) {
        log(
          'Workaround toggle is on, but neither prototype member existed to delete.',
          'info'
        );
      }
      if (!navigator.xr) {
        log('navigator.xr is undefined on this device/browser', 'err');
      } else {
        navigator.xr
          .isSessionSupported('immersive-ar')
          .then((supported) =>
            log(
              `immersive-ar supported: ${supported}`,
              supported ? 'ok' : 'err'
            )
          )
          .catch((err) => log(`isSessionSupported error: ${err}`, 'err'));
      }
    